import { IssueSyncRow, SERVER_SAFETY_LAG_MS } from './issue-sync-models';

// Writes return 202 and apply asynchronously: the request publishes an event, a
// subscriber writes Mongo, and the change comes back through the feed. A delta can
// arrive inside that round trip carrying the row's pre-write state, overwrite the
// optimistic patch, and make the Issue un-fix on screen and fix again.
//
// A shadow per issue id is what prevents it. While a write is pending, the fields
// the caller patched are re-applied over any incoming row, so the delta cannot undo
// them. Fields the caller did not touch still update — suppressing the whole row
// would freeze unrelated columns like the vehicle's last movement for the duration.

// The sync poll's cadence. Exported because the write timeout below is defined against
// it: a confirmation slower than one poll tells the user nothing the poll would not have
// delivered anyway. Two copies would let the cadence move without the bound following.
export const POLL_INTERVAL_MS = 5 * 60 * 1000;

// Decides whether an incoming delta represents the write landing. A close is
// confirmed by the row being removed; an edit by a field reaching its new value.
export type WriteConfirmation = (row: IssueSyncRow | null, removed: boolean) => boolean;

export interface PendingWrite {
  issueId: string;
  patch: Partial<IssueSyncRow>;
  startedAtMs: number;
  confirms: WriteConfirmation;
  // A close is confirmed by the row leaving and by nothing else. It patches no field, so a
  // merged shadow that only compared fields would confirm it against a row that is still
  // there — and with no fields at all, against any row.
  requiresRemoval: boolean;
}

export type WriteOutcome = 'confirmed' | 'timedOut';

export interface SettledWrite {
  issueId: string;
  outcome: WriteOutcome;
  patch: Partial<IssueSyncRow>;
}

export interface Clock {
  now(): number;
}

export const systemClock: Clock = { now: () => Date.now() };

export class IssuePendingWrites {
  private readonly pending = new Map<string, PendingWrite>();

  // The projection's share of confirmation latency, measured rather than assumed.
  // Seeded at the safety lag because a write cannot confirm faster than that, and no
  // smaller figure is defensible until this session has actually timed one. Each
  // confirmed write raises the mark; it never lowers, so one slow projection cycle
  // does not strand the next write on an optimistic figure.
  private projectionLagHighWaterMs = SERVER_SAFETY_LAG_MS;

  // The smallest lag the feed has been observed to hold rows back by. The compiled-in
  // figure is only a starting point: if the server's lag is ever raised, a timeout
  // built on the old number would sit below the fastest possible confirmation and
  // roll every write back as failed. That cannot be recovered from by watching
  // confirmations, because a write that fails spuriously never confirms and so never
  // teaches anything. Reading the lag off the responses themselves is what closes it.
  private smallestObservedLagMs = Number.POSITIVE_INFINITY;

  constructor(private readonly clock: Clock = systemClock) {}

  // The lag the feed withholds every row for. Never below the figure the server is
  // known to use, so a lowered lag costs a slightly long timeout rather than a wrong
  // one.
  safetyLagMs(): number {
    if (this.smallestObservedLagMs === Number.POSITIVE_INFINITY) return SERVER_SAFETY_LAG_MS;
    return Math.max(SERVER_SAFETY_LAG_MS, this.smallestObservedLagMs);
  }

  // Every response carries the time it was assembled and the rows it chose to send,
  // and the server only sends rows older than the lag. The gap between the two is
  // therefore never smaller than the lag, which makes each reading an overestimate and
  // the smallest reading the closest one. Overestimating is the safe direction: too
  // long delays a failure report, too short invents one.
  observeFeedLag(asOfUtcMs: number, newestRowUtcMs: number): void {
    const gap = asOfUtcMs - newestRowUtcMs;
    if (!Number.isFinite(gap) || gap <= 0) return;
    // A tenant whose newest row is days old says nothing about the lag, and the
    // ordinary delta has answered long before this anyway.
    if (gap > POLL_INTERVAL_MS) return;
    if (gap < this.smallestObservedLagMs) this.smallestObservedLagMs = gap;
  }

  // The wait before a patch is rolled back. Two terms, both real: the lag the feed
  // withholds every row for, and how long the projection takes to publish one. It is
  // bounded above by the poll interval plus that lag, because past there the routine
  // delta has already answered and holding a failed action on screen only hides it.
  timeoutMs(): number {
    const lag = this.safetyLagMs();
    return Math.min(lag + this.projectionLagHighWaterMs, POLL_INTERVAL_MS + lag);
  }

  isPending(issueId: string): boolean {
    return this.pending.has(issueId);
  }

  pendingIds(): string[] {
    return [...this.pending.keys()];
  }

  // Registers an optimistic patch. A second write to the same row merges into the
  // existing shadow and keeps the original start time, so a rapid pair of edits
  // cannot keep extending its own deadline indefinitely.
  begin(
    issueId: string,
    patch: Partial<IssueSyncRow>,
    confirms: WriteConfirmation,
    requiresRemoval = false,
  ): void {
    const existing = this.pending.get(issueId);
    const mergedPatch = { ...(existing?.patch ?? {}), ...patch };
    // Once any write in the shadow needs the row gone, the whole shadow does.
    const removalRequired = (existing?.requiresRemoval ?? false) || requiresRemoval;
    // What settles a merged shadow is the merged patch being reflected — not either
    // predicate on its own. Keeping only the newer one would settle the earlier write's
    // fields the moment the later landed, and they would appear to revert. Requiring both
    // is worse: rewrite one field twice and the pair asks for two values of it at once,
    // which no row can satisfy, so a write that landed reports as timed out.
    //
    // A row that left settles the whole shadow, with no predicate consulted: there are no
    // fields left to compare, and holding it would report a rollback for a write the server
    // has already acted on.
    const confirmsMerged: WriteConfirmation = existing
      ? (row, removed) => {
        if (removed) return true;
        // A close in the shadow can only be answered by the row going away.
        if (removalRequired) return false;
        const fields = Object.entries(mergedPatch);
        // No fields to compare is not the same as every field matching.
        return fields.length > 0 && !!row
          && fields.every(([key, value]) => (row as never)[key] === value);
      }
      : confirms;
    this.pending.set(issueId, {
      issueId,
      patch: mergedPatch,
      startedAtMs: existing?.startedAtMs ?? this.clock.now(),
      confirms: confirmsMerged,
      requiresRemoval: removalRequired,
    });
  }

  // Applies the optimistic patch to a row the store already holds, so the caller
  // sees its own action at once.
  patched(row: IssueSyncRow): IssueSyncRow {
    const write = this.pending.get(row.id);
    return write ? { ...row, ...write.patch } : row;
  }

  // Reports whether a delta settles the write for this row, and clears the shadow
  // when it does. A removal counts: closing an Issue is confirmed by its absence.
  settle(issueId: string, row: IssueSyncRow | null, removed: boolean): boolean {
    const write = this.pending.get(issueId);
    if (!write) return false;
    if (!write.confirms(row, removed)) return false;
    this.pending.delete(issueId);
    this.recordLatency(this.clock.now() - write.startedAtMs);
    return true;
  }

  // Writes whose deadline has passed. The caller rolls each patch back and reports
  // the failure — a write that never confirms must not sit on screen looking applied.
  takeExpired(): SettledWrite[] {
    const now = this.clock.now();
    const deadline = this.timeoutMs();
    const expired: SettledWrite[] = [];
    for (const write of [...this.pending.values()]) {
      if (now - write.startedAtMs < deadline) continue;
      this.pending.delete(write.issueId);
      expired.push({ issueId: write.issueId, outcome: 'timedOut', patch: write.patch });
    }
    return expired;
  }

  // Drops shadows for rows that have left the store for a reason unrelated to the
  // write — permission loss being the one that matters. This is neither a
  // confirmation nor a failure, so the confirmation predicate is not consulted and
  // nothing is reported: the row is simply no longer the caller's to hold. Leaving
  // the shadow would later report a write failure for a row that was revoked.
  abandon(issueIds: string[]): void {
    for (const id of issueIds) this.pending.delete(id);
  }

  // Abandons every shadow without reporting a failure. For a tenant switch or a
  // sign-out, where the rows the patches referred to are no longer addressable.
  clear(): void {
    this.pending.clear();
  }

  // The observed latency covers the projection and the safety lag together; only the
  // remainder above the lag is the projection's, and it cannot be negative.
  private recordLatency(observedMs: number): void {
    const projection = Math.max(0, observedMs - this.safetyLagMs());
    if (projection > this.projectionLagHighWaterMs) {
      this.projectionLagHighWaterMs = projection;
    }
  }
}
